import { type GuidanceSession } from '@fruitful/types';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CopyIcon,
    ScrollView,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { TextError } from '../../common/components/InlineError';
import { MutationSuccessToastOptions } from '../../common/components/Toasts';
import {
    formatToSimpleDateTimeString,
    getPersonDisplayName,
} from '../../common/utils/formatting';
import type { MembershipDetails } from '../../entities/entityTypes';
import { useGetGuidanceSessionTranscript } from '../hooks/useGetGuidanceSessionTranscript';
import { getMembershipGuidanceSessionDisplayName } from '../utils/membershipGuidanceSessionDisplayName';

interface GuidanceSessionTranscriptModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: GuidanceSession;
    membershipDetails: MembershipDetails;
}

const getSessionAttendeeLines = (
    membershipDetails: MembershipDetails,
): string[] => {
    const guideName = getPersonDisplayName(membershipDetails.guide);
    const memberNames = membershipDetails.users
        .map((user) => getPersonDisplayName(user, ''))
        .filter((name) => name !== '')
        .join(', ');

    const lines = [`Guide: ${guideName}`];
    if (memberNames !== '') {
        lines.push(`Members: ${memberNames}`);
    }
    return lines;
};

export const GuidanceSessionTranscriptModal = ({
    isOpen,
    onClose,
    session,
    membershipDetails,
}: GuidanceSessionTranscriptModalProps) => {
    const toast = useToastController();
    const { data, isLoading, error } = useGetGuidanceSessionTranscript(
        { id: session.id },
        { enabled: isOpen },
    );
    const sessionType = getMembershipGuidanceSessionDisplayName(
        session.type,
        membershipDetails,
    );
    const sessionDate = formatToSimpleDateTimeString(session.startTime);
    const attendeeLines = getSessionAttendeeLines(membershipDetails);

    const handleCopyTranscript = () => {
        if (data?.transcript != null) {
            const formattedText = [
                `${sessionType} transcript on ${sessionDate}`,
                ...attendeeLines,
                '',
                data.transcript,
            ].join('\n');

            void navigator.clipboard.writeText(formattedText);
            toast.show('Transcript copied!', MutationSuccessToastOptions);
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <YStack width={900} margin={16} gap={16}>
                <XStack
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={12}
                    paddingRight={48}
                >
                    <YStack gap={4} flex={1}>
                        <BodyLargeEmphasized>
                            {sessionType} transcript on {sessionDate}
                        </BodyLargeEmphasized>
                        {attendeeLines.map((line) => (
                            <BodyMediumRegular key={line} color="$gray1000">
                                {line}
                            </BodyMediumRegular>
                        ))}
                    </YStack>
                    {!isLoading &&
                        error == null &&
                        data?.transcript != null && (
                            <Button
                                text="Copy"
                                icon={CopyIcon}
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                onPress={handleCopyTranscript}
                                width="auto"
                            />
                        )}
                </XStack>

                {isLoading && (
                    <YStack padding={24} alignItems="center">
                        <Spinner />
                    </YStack>
                )}

                {!isLoading && error != null && (
                    <TextError message={error.message} />
                )}

                {!isLoading && error == null && data == null && (
                    <BodyMediumRegular color="$gray1000">
                        No transcript available for this session.
                    </BodyMediumRegular>
                )}

                {!isLoading && error == null && data != null && (
                    <ScrollView maxHeight={600}>
                        <BodyMediumRegular style={{ whiteSpace: 'pre-wrap' }}>
                            {data.transcript}
                        </BodyMediumRegular>
                    </ScrollView>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
